/*
GPS DataViz

Copyright © 2021 GPSDataViz

Permission is hereby granted, free of charge, to any person obtaining a copy of this software and associated documentation files (the "Software"), to deal in the Software without restriction, including without limitation the rights to use, copy, modify, merge, publish, distribute, sublicense, and/or sell copies of the Software, and to permit persons to whom the Software is furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM, OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE SOFTWARE.
*/
import React, { useEffect, useState } from "react";

import {
  Route,
  Routes,
  useLocation,
  useNavigate
} from "react-router-dom";
import { datadogRum } from '@datadog/browser-rum';
import { ROUTES, CHATBOT_PATH } from "./routes";

import { Auth } from "./Components/Auth/Auth";
import { TrainingAPI } from "./api/TrainingAPI";

import { Button } from "antd";
import { audioMessage as message } from "./Components/Common/Notifications/audioMessage";

import { PostData } from "./Components/Upload/PostData";
import { TeamData } from "./Components/Team/TeamData";
import PlayerData from "./Components/Player/PlayerData";
import { HelpCenterPage } from "./Components/HelpCenter/HelpCenterPage";
import Analysis from "./Components/Analysis";
import Leaderboard from "./Components/Leaderboard/Leaderboard";
import Scatterplot from "./Components/Scatterplot/Scatterplot";
import { ProfileOutlined } from "@ant-design/icons";

import { Login } from "./Components/Login";

import { Overview } from "./Components/Overview/Overview";
import { StatusTracker } from "./Components/StatusTracker/StatusTracker";
import { StatusTrackerV2 } from "./Components/StatusTrackerV2";


import "./App.css";
import { TeamSelection } from "./Components/TeamSelection/TeamSelection";
import { OverviewV2 } from "./Components/OverviewV2/OverviewV2";
import { OverviewStatusTracker } from "./Components/OverviewV3/OverviewStatusTracker";
import { UploadData } from "./Components/UploadV2/UploadData";
import { useSelector, useDispatch } from "react-redux";
import { setMiscConstants, validateTeamAndNotify } from "./store/actions/team.actions";
import posthog from 'posthog-js';
import { useAuth0 } from "@auth0/auth0-react";
import { setAsymForceplateMetrics, setNonAsymExercises } from "./Utils/chart_utils"
import ChatView from "@components/Chatbot/ChatView";
import { useDataAccess } from "@components/PermissionedComponents";
import ChatViewV2 from "@components/ChatbotV2/ChatView";
import FloatingChatRoot from "@components/ChatbotV2/FloatingChat/FloatingChatRoot";
import { Banners } from "@components/Common/Banners";

// The Status Tracker menu item lands here. Team info carries the V2 gate: true goes straight
// to V2, false straight to V1. It is undefined on an API that predates the field, and that
// must not read as "off" — a gated team would lose V2 — so we try V2 and let its 403 answer.
// Defined here rather than in the V2 module so that module stays free of any dependency on V1.
const StatusTrackerPage = () => {
  const v2Gate = useSelector(({ team }) => team?.info?.status_tracker_v2_enabled);
  const [v2Failed, setV2Failed] = useState(false);
  return v2Gate === false || v2Failed
    ? <StatusTracker />
    : <StatusTrackerV2 onUnavailable={() => setV2Failed(true)} />;
};


// React functional component

const contentMap = (str, close_popout) => {
  switch (str) {
    case "overview":
      return (
        <div>
          <p className="overview-modal-text">
            The calendar uses periodization and colors to indicate how difficult
            each training and game is compared to baselines
          </p>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder1">G</span>
            <p className="placeholder1-text">Filled in G is a Game</p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder2">G</span>
            <p className="placeholder1-text">
              Non Filled in G is a Future Game
            </p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder3">G</span>
            <p className="placeholder1-text">Filled in T is a Training </p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder4">G</span>
            <p className="placeholder1-text">
              Non Filled T is the suggested periodization{" "}
            </p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder5"></span>
            <p className="placeholder1-text">
              Red represents over 55% of team baseline
            </p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder6"></span>
            <p className="placeholder1-text">
              Yellow represents 30%-55% of team baseline
            </p>
          </div>
          <div className="placeholder-wrapper">
            <span className="icon-placeholder7"></span>
            <p className="placeholder1-text">
              Green represents below 30% of team baseline
            </p>
          </div>
          <div className="button-wrapper">
            <button
              className="overview-modal-button"
              onClick={() => {
                close_popout();
              }}
            >
              Got it
            </button>
          </div>
        </div>
      );
    default:
      return <div>Placeholder Data Default </div>;
  }
};
const App = () => {
  let today = new Date(Date.now());
  let month_back = new Date(Date.now());
  const overviewVersion = localStorage.getItem("overview_version") || 2;
  month_back.setMonth(month_back.getMonth() - 1);
  let month_back_str =
    month_back.getFullYear() +
    "-" +
    String(month_back.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(month_back.getDate()).padStart(2, "0");
  let today_str =
    today.getFullYear() +
    "-" +
    String(today.getMonth() + 1).padStart(2, "0") +
    "-" +
    String(today.getDate()).padStart(2, "0");

  const {
    info: { dataviz_lite, display_name: teamName, sport },
  } = useSelector(({ team }) => team);
  const { user } = useAuth0();
  const { currentUser } = useSelector(({ auth }) => auth);
  const [loggedIn, setLoggedIn] = useState(true);
  const [loading, setLoading] = useState(false);
  const [popoutModalContent, setPopoutModalContent] = useState(null);

  const [teamAvgStartDate, setTeamAvgStartDate] = useState(month_back_str);
  const [teamAvgEndDate, setTeamAvgEndDate] = useState(today_str);
  const [teamAvgFpData, setTeamAvgFpData] = useState(undefined);
  const [teamAvgFpMetric, setTeamAvgFpMetric] = useState([
    "jump_height",
    "mRSI",
    "load",
    "explode",
  ]);
  const [teamAvgWeekly, setTeamAvgWeekly] = useState(false);
  const [teamAvgPositionFilter, setTeamAvgPositionFilter] = useState(undefined);
  const [testingDataStats, setTestingDataStats] = useState(false);
  const [overviewACWRDate, setOverviewACWRDate] = useState(today_str);
  const [overviewACWRData, setOverviewACWRData] = useState(null);
  const [gotCalData, setGotCalData] = useState(false);
  const [overviewShowWeekly, setOverviewShowWeekly] = useState(false);
  const [showCalenderPopout, setShowCalenderPopout] = useState(false);

  const [players, setPlayers] = useState([]);
  const [playerDataPracticePlan, setPlayerDataPracticePlan] = useState([]);
  const [playerDataCostPerMin, setPlayerDataCostPerMin] = useState([]);
  const [playerDataData, setPlayerDataData] = useState([]);
  const [playerDataFpData, setPlayerDataFpData] = useState(undefined);
  const [playerDataFpMetric, setPlayerDataFpMetric] = useState([
    "jump_height",
    "mRSI",
    "load",
    "explode",
  ]);
  const [playerDataTestingData, setPlayerDataTestingData] = useState(undefined);
  const [playerDataTestingOptions, setPlayerDataTestingOptions] = useState([]);
  const [playerDataTestingRadarData, setPlayerDataTestingRadarData] = useState(
    []
  );
  const [playerDataTestingRadarMetrics, setPlayerDataTestingRadarMetrics] =
    useState([]);
  const [playerDataAvgPercChange, setPlayerDataAvgPercChange] = useState(0);
  const [playerDataLastPercChange, setPlayerDataLastPercChange] = useState(0);
  const [playerDataName, setPlayerDataName] = useState(undefined);
  const [playerDataPlayerId, setPlayerDataPlayerId] = useState(undefined);
  const [playerDataStartDate, setPlayerDataStartDate] =
    useState(month_back_str);
  const [playerDataEndDate, setPlayerDataEndDate] = useState(today_str);
  const [playerDataWeekly, setPlayerDataWeekly] = useState(false);
  const [gameToggled, setGameToggled] = useState(false);

  const [analysis, setAnalysis] = useState([]);
  const [analysisDate, setAnalysisDate] = useState("");
  const [analysisOverworked, setAnalysisOverworked] = useState([]);
  const [analysisUnderworked, setAnalysisUnderworked] = useState([]);
  const [analysisVisible, setAnalysisVisible] = useState(false);

  const dispatch = useDispatch();

  const { canAccess: canAccessData } = useDataAccess();

  const identify_user_for_posthog = () => {
    try {
      posthog.identify(user?.email, {
        email: user?.email,
        name: user?.name,
        team: teamName
      });
      console.log("Identified user for posthog", user?.email)
    } catch (err) {
      console.error(err);
      console.log("--", user)
    }
  }

  // Same usr/account shape as the mobile app, so one person's web and mobile
  // events carry the same attributes in Datadog.
  useEffect(() => {
    if (!currentUser?.uuid) return;
    datadogRum.setUser({
      id: currentUser.email,
      email: currentUser.email,
      name: `${currentUser.first_name} ${currentUser.last_name}`,
      team: currentUser.team_name,
      team_id: currentUser.team,
      user_type: currentUser.user_type,
      sport,
    });
    datadogRum.setAccount({ id: String(currentUser.team), name: currentUser.team_name });
  }, [currentUser, sport]);

  useEffect(() => {
    if (teamName) {
      identify_user_for_posthog();
      get_constants()
      dispatch(validateTeamAndNotify())
    }
  }, [teamName]);


  let trainingAPI = new TrainingAPI();

  const set_training_api = () => {
    trainingAPI = new TrainingAPI();
  };

  const get_constants = async () => {
    try {
      const data = await trainingAPI.getMiscellaneousConstants();
      dispatch(setMiscConstants(data))
      setAsymForceplateMetrics(data?.asym_forceplate_metrics)
      setNonAsymExercises(data?.non_asym_exercises)
    } catch (error) {
      console.error("Error fetching misc constants")
    }
  }

  const toggle_overview_show_weekly = () => {
    setOverviewShowWeekly(!overviewShowWeekly);
  };

  useEffect(() => {
    console.log('App mounted');
    return () => {
      console.log('App unmounted');
    };
  }, []);

  const team_avg_email = () => {
    trainingAPI
      .emailOverviewCharts(teamAvgStartDate, teamAvgEndDate, teamAvgWeekly)
      .then((x) => {
        message.success("Email sent!");
      })
      .catch((x) => message.error("Error sending email."));
  };

  const is_game_toggled = (checked, event) => {
    setGameToggled(checked);
  };

  //Overview page
  const overview_acwr_date_change = (value, dateString) => {
    setOverviewACWRDate(dateString === "" ? null : dateString);
  };

  const team_acwr_get = (extraParams = {}) => {
    // ACWR is GPS-derived; the endpoint 403s without AccessGpsData.
    if (!canAccessData("gps")) return;

    setLoading(true);
    trainingAPI
      .getTeamACWR({ date: overviewACWRDate, ...extraParams })
      .then((data) => {
        setOverviewACWRData(data);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const cal_data_got = () => {
    setGotCalData(true);
  };

  const reset_cal = () => {
    setGotCalData(false);
  };

  //Player data page
  const player_data_player_id_change = (player_id, player_name) => {
    setPlayerDataPlayerId(player_id);
    setPlayerDataName(player_name);
    setLoading(true);
    trainingAPI.getDrillCostPerMin(player_id).then((data) => {
      setPlayerDataCostPerMin(data);
      setLoading(false);
    });
  };

  const player_data_date_change = (value, dateString) => {
    setPlayerDataStartDate(dateString[0] === "" ? null : dateString[0]);
    setPlayerDataEndDate(dateString[1] === "" ? null : dateString[1]);
  };

  const player_data_from_acwr = (name) => {
    const player = players.find(
      (p) => `${p.first_name} ${p.last_name}` === name
    );
    let acwr_date = overviewACWRDate;
    let split_date = acwr_date.split("-");
    let end_date = new Date();
    end_date.setMonth(split_date[1] - 1);
    end_date.setDate(split_date[2]);
    end_date.setFullYear(split_date[0]);

    let start_date = new Date(end_date);
    start_date.setMonth(start_date.getMonth() - 1);
    setPlayerDataPlayerId(player?.player_id);
    setPlayerDataName(name);
    setPlayerDataStartDate(
      start_date.getFullYear() +
        "-" +
        (start_date.getMonth() + 1) +
        "-" +
        start_date.getDate()
    );
    setPlayerDataEndDate(
      end_date.getFullYear() +
        "-" +
        (end_date.getMonth() + 1) +
        "-" +
        end_date.getDate()
    );
  };

  const player_data_post_data = (name, date) => {
    let acwr_date = date;
    let split_date = acwr_date.split("-");
    let end_date = new Date();
    end_date.setMonth(split_date[1] - 1);
    end_date.setDate(split_date[2]);
    end_date.setFullYear(split_date[0]);

    let start_date = new Date(end_date);
    start_date.setMonth(start_date.getMonth() - 1);
    setPlayerDataName(name);
    setPlayerDataStartDate(
      start_date.getFullYear() +
        "-" +
        (start_date.getMonth() + 1) +
        "-" +
        start_date.getDate()
    );
    setPlayerDataEndDate(
      end_date.getFullYear() +
        "-" +
        (end_date.getMonth() + 1) +
        "-" +
        end_date.getDate()
    );
  };

  const player_data_weekly_change = (checked, event) => {
    setPlayerDataWeekly(checked);
  };

  const close_popout = () => {
    setShowCalenderPopout(false);
  };

  const player_data_email = () => {
    trainingAPI
      .emailPlayerCharts(
        playerDataName,
        playerDataStartDate,
        playerDataEndDate,
        playerDataWeekly
      )
      .then((x) => message.success("Email sent!"));
  };

  const location = useLocation();
  const navigate = useNavigate();
  const searchParams = new URLSearchParams(location.search);
  if (searchParams.get("returnTo")){
    console.log("Redirecting to ", searchParams.get("returnTo"));
    navigate(searchParams.get("returnTo"));
  }

  //analysis
  // const  toggle_analysis_visible = () => {
  //   if (this.state.analysis_visible) {
  //     this.setState({ analysis_visible: false });
  //   } else {
  //     this.setState({ analysis_visible: true });
  //   }
  // };

  const set_analysis_data = (
    analysis,
    analysis_date,
    analysis_overworked,
    analysis_underworked
  ) => {
    setAnalysis(analysis);
    setAnalysisDate(analysis_date);
    setAnalysisOverworked(analysis_overworked);
    setAnalysisUnderworked(analysis_underworked);
  };

  const dispatchDropdowns = (types, data) => {
    dispatch({
      type: 'UPDATE_DROPDOWNS',
      payload: {
        types,
        data
      }
    });
  };

  return (
    <div className="App">
      <Banners />
      <Routes>
        {ROUTES.map((route, index) => (
          <Route exact key={index} {...route}></Route>
        ))}

        <Route
          exact
          path="/login"
          element={<Login set_training_api={set_training_api} />}
        />

        <Route
          exact
          path="/upload-data"
          element={
            <UploadData
              reset_cal={reset_cal}
              const
              set_analysis_data={set_analysis_data}
            />
          }
        />

        {/* <Route
          exact
          path="/postdata"
          element={
            <PostData
              reset_cal={reset_cal}
              const
              set_analysis_data={set_analysis_data}
              dispatchDropdowns={dispatchDropdowns} 
            />
          }
        /> */}

        <Route
          exact
          path="/playerdata"
          element={
            <PlayerData
              practicePlan={playerDataPracticePlan}
              costPerMin={playerDataCostPerMin}
              players={players}
              onDateChange={player_data_date_change}
              onWeeklyChange={player_data_weekly_change}
              onNameChange={player_data_player_id_change}
              data={playerDataData}
              start_date={playerDataStartDate}
              end_date={playerDataEndDate}
              name={playerDataName}
              player_id={playerDataPlayerId}
              const
              email={player_data_email}
              fromACWR={player_data_from_acwr}
              fromPostData={player_data_post_data}
              weekly={playerDataWeekly}
              set_team_fp_default_metrics={setPlayerDataFpMetric}
              fp_data={playerDataFpData}
              fp_metrics={playerDataFpMetric}
              testing_options={playerDataTestingOptions}
              testing_data={playerDataTestingData}
              avg_perc_change={playerDataAvgPercChange}
              last_perc_change={playerDataLastPercChange}
              onGameToggled={is_game_toggled}
              gameToggled={gameToggled}
              loading={loading}
            />
          }
        />

        <Route
          exact
          path="/overview"
          element={
            dataviz_lite ? <OverviewV2 /> :
            (
              overviewVersion == 1 ? (
                <Overview
                  loggedIn={loggedIn}
                  popoutModalContent={popoutModalContent}
                  showCalenderPopout={showCalenderPopout}
                  close_popout={close_popout}
                  acwr_get={team_acwr_get}
                  loading={loading}
                  onDateChange={overview_acwr_date_change}
                  show_weekly={overviewShowWeekly}
                  toggle_overview_show_weekly={toggle_overview_show_weekly}
                  acwr_data={overviewACWRData}
                  date={overviewACWRDate}
                  got_cal_data={gotCalData}
                  // cal_data_got={cal_data_got} //commented the line for now in case we want to have the data reload flag back
                />
              )
              :
              (
                <OverviewStatusTracker
                  loggedIn={loggedIn}
                  popoutModalContent={popoutModalContent}
                  showCalenderPopout={showCalenderPopout}
                  close_popout={close_popout}
                  acwr_get={team_acwr_get}
                  loading={loading}
                  onDateChange={overview_acwr_date_change}
                  show_weekly={overviewShowWeekly}
                  toggle_overview_show_weekly={toggle_overview_show_weekly}
                  acwr_data={overviewACWRData}
                  date={overviewACWRDate}
                  got_cal_data={gotCalData}
                  // cal_data_got={cal_data_got} //commented the line for now in case we want to have the data reload flag back
                />
              )
            )
              
          }
        />
        <Route
          exact
          path="/status-tracker"
          element={
            <StatusTracker
              loggedIn={loggedIn}
              popoutModalContent={popoutModalContent}
              showCalenderPopout={showCalenderPopout}
              close_popout={close_popout}
              acwr_get={team_acwr_get}
              loading={loading}
              onDateChange={overview_acwr_date_change}
              show_weekly={overviewShowWeekly}
              toggle_overview_show_weekly={toggle_overview_show_weekly}
              acwr_data={overviewACWRData}
              date={overviewACWRDate}
              got_cal_data={gotCalData}
              // cal_data_got={cal_data_got} //commented the line for now in case we want to have the data reload flag back
            />
          }
        />
        <Route exact path="/status-tracker-v2" element={<StatusTrackerPage />} />

        <Route exact path="/team-selection" element={<TeamSelection />} />
        <Route exact path="/help-center" element={<HelpCenterPage />} />
        <Route
          exact
          path="/teamdata"
          element={
            <TeamData
              set_team_fp_default_metrics={setTeamAvgFpMetric}
              fp_data={teamAvgFpData}
              fp_metrics={teamAvgFpMetric}
            />
          }
        />
        <Route exact path="/leaderboard" element={<Leaderboard />} />
        <Route exact path="/scatterplot" element={<Scatterplot />} />
        <Route exact path={CHATBOT_PATH} element={<ChatViewV2 />} />
      </Routes>
      <FloatingChatRoot />
    </div>
  );
};

export default App;
